import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, ArrowLeft, CheckCircle2, XCircle, Quote } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWhoSaidDaily, submitWhoSaid } from '../../services/gamesApi';
import GameResultCard from './GameResultCard';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Who Said It? — 5 quotes/day, match the biblical figure who said them.
 */
const WhoSaidItGame = ({ language = 'en', onBack, onScored }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchWhoSaidDaily(language)
      .then(setData)
      .catch(() => toast.error(t('games.failedLoad')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const allAnswered = data && data.questions.every((q) => picks[q.id] !== undefined);

  const onSubmit = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const answers = data.questions.map((q) => ({ id: q.id, picked: picks[q.id] }));
      const r = await submitWhoSaid(answers);
      setResult(r);
      onScored?.(r);
    } catch {
      toast.error(t('games.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('games.loading')}
      </div>
    );
  }
  if (!data) return null;

  if (result) {
    const detailsById = Object.fromEntries(result.details.map((d) => [d.id, d]));
    return (
      <div className="space-y-4" data-testid="whosaidit-result-screen">
        <Button onClick={onBack} variant="ghost" size="sm" className="-ml-2">
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('games.back')}
        </Button>
        <GameResultCard
          language={language}
          gameTitle={t('games.whosaid.title')}
          correct={result.correct}
          total={result.total}
          score={result.score}
          perfect={result.perfect}
          streak={result.streak}
          newTotal={result.new_total}
        />
        <div className="space-y-2">
          {data.questions.map((q, idx) => {
            const d = detailsById[q.id];
            return (
              <Card key={q.id} className="border" data-testid={`whosaidit-review-${q.id}`}>
                <CardContent className="p-3 text-sm">
                  <div className="flex items-start gap-2">
                    {d?.correct
                      ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                    <div>
                      <p className="font-medium italic">{idx + 1}. "{q.quote}"</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('games.whosaid.answer')}{' '}
                        <span className="font-semibold">{q.options[d?.answer_index]}</span>
                        {d?.reference && <> · <span className="italic">{d.reference}</span></>}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="whosaidit-game-screen">
      <Button onClick={onBack} variant="ghost" size="sm" className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" />
        {t('games.back')}
      </Button>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">
          {t('games.whosaid.title')}
        </h2>
        <Badge variant="outline" className="text-[10px]">
          {data.date} · {t('games.whosaid.quotes5')}
        </Badge>
      </div>

      {data.questions.map((q, idx) => (
        <Card key={q.id} className="border" data-testid={`whosaidit-q-${q.id}`}>
          <CardContent className="p-4 space-y-3 text-sm">
            <div className="flex items-start gap-2">
              <Quote className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
              <p className="italic font-medium">{idx + 1}. "{q.quote}"</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {q.options.map((o, i) => {
                const isPicked = picks[q.id] === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPicks((p) => ({ ...p, [q.id]: i }))}
                    data-testid={`whosaidit-opt-${q.id}-${i}`}
                    className={`text-left px-3 py-2 rounded-md border text-xs transition-colors ${
                      isPicked
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {o}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      <Button
        onClick={onSubmit}
        disabled={!allAnswered || submitting}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
        data-testid="whosaidit-submit-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        {t('games.submitAnswers')}
      </Button>
    </div>
  );
};

export default WhoSaidItGame;
